/* CuisinePage — SEO landing page for /cuisine/:slug (e.g. /cuisine/italian). */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import RecipeCard from "@/components/RecipeCard";
import PageMeta from "@/components/PageMeta";
import { recipePath } from "@/lib/slug";

function useJsonLd(id, obj) {
  useEffect(() => {
    if (!obj) return undefined;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    el.text = JSON.stringify(obj);
    document.head.appendChild(el);
    return () => el.remove();
  }, [id, obj]);
}

function FaqList({ name, faqs }) {
  if (!faqs.length) return null;
  return (
    <section aria-labelledby="cuisine-faq-heading" className="mt-16 max-w-3xl" data-testid="cuisine-faqs">
      <h2 id="cuisine-faq-heading" className="font-display text-2xl font-black text-rf-ink mb-4">{name} cooking questions</h2>
      <div className="divide-y divide-rf-ink/10 border-y border-rf-ink/10">
        {faqs.map((f, i) => (
          <details key={i} className="group py-4" data-testid={`cuisine-faq-${i}`} open={i === 0}>
            <summary className="cursor-pointer font-bold text-rf-ink list-none flex justify-between gap-4">
              {f.q}<span className="text-rf-orange group-open:rotate-45 transition-transform" aria-hidden="true">+</span>
            </summary>
            <p className="mt-2 text-rf-ink-soft leading-relaxed">{f.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

export function CuisineChips({ rows, testidPrefix = "cuisine-other" }) {
  return (
    <div className="flex flex-wrap gap-2">
      {rows.map((c) => (
        <Link key={c.slug} to={`/cuisine/${c.slug}`} data-testid={`${testidPrefix}-${c.slug}`}
              className="inline-flex items-baseline gap-1.5 rounded-full border border-rf-ink/15 bg-white px-4 py-2 font-bold text-rf-ink hover:border-rf-orange hover:text-rf-orange transition-colors">
          {c.cuisine} <span className="text-xs text-rf-ink-soft font-semibold">{c.count}</span>
        </Link>
      ))}
    </div>
  );
}

export default function CuisinePage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    window.scrollTo(0, 0);
    api.get(`/directory/cuisine/${encodeURIComponent(slug)}`).then((r) => setData(r.data)).catch(() => setData({ cuisine: slug, recipes: [], faqs: [], others: [] }));
  }, [slug]);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const listLd = useMemo(() => (data?.recipes?.length ? {
    "@context": "https://schema.org", "@type": "CollectionPage", name: `${data.cuisine} recipes`,
    ...(data.hero_url ? { image: `${origin}${data.hero_url}` } : {}),
    url: `${origin}/cuisine/${data.slug}`, description: data.intro || `${data.recipes.length} ${data.cuisine} family recipes on Recipe Filer.`,
    isPartOf: { "@type": "WebSite", name: "Recipe Filer", url: "https://recipefiler.com" },
    mainEntity: { "@type": "ItemList", numberOfItems: data.recipes.length,
      itemListElement: data.recipes.map((r, i) => ({ "@type": "ListItem", position: i + 1, url: `${origin}${recipePath(r)}`, name: r.title })) },
  } : null), [data, origin]);
  const faqLd = useMemo(() => (data?.faqs?.length ? {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: data.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  } : null), [data]);
  useJsonLd("rf-cuisine-jsonld", listLd);
  useJsonLd("rf-cuisine-faq-jsonld", faqLd);

  if (!data) {
    const label = slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    return <div className="min-h-[60vh] flex flex-col items-center justify-center" data-testid="cuisine-loading"><PageMeta title={`${label} recipes from family kitchens`} canonical={`/cuisine/${slug}`} /><h1 className="sr-only">{label} recipes</h1><div className="w-8 h-8 border-2 border-rf-orange border-t-transparent rounded-full animate-spin" /></div>;
  }
  const { cuisine, recipes } = data;
  const n = recipes.length;
  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-20">
      <PageMeta
        title={`${cuisine} recipes: ${n} from family kitchens`}
        description={data.intro ? data.intro.slice(0, 155) : `Browse ${n} ${cuisine} ${n === 1 ? "recipe" : "recipes"} from real family cookbooks on Recipe Filer. Free to read and share.`}
        ogImage={data.hero_url || recipes[0]?.images?.[0]}
      />
      <div className="flex gap-5 mb-4">
        <Link to="/directory" className="text-sm font-bold text-rf-orange hover:underline">← All recipes</Link>
        <Link to="/cuisine" data-testid="cuisine-all-link" className="text-sm font-bold text-rf-orange hover:underline">All cuisines</Link>
      </div>
      <p className="overline mb-3">Cuisine</p>
      <h1 className="font-display text-5xl md:text-7xl font-black text-rf-ink mb-6 leading-[0.95]" data-testid="cuisine-heading">{cuisine} recipes</h1>
      {data.hero_url && (
        <div className="relative mb-10 rounded-3xl overflow-hidden aspect-[16/9] md:aspect-[21/9] bg-rf-cream-alt shadow-xl" data-testid="cuisine-hero">
          <img src={data.hero_url} alt={data.hero_alt || `A table of classic ${cuisine} dishes`} className="w-full h-full object-cover" fetchpriority="high" />
          <div className="absolute inset-0 bg-gradient-to-t from-rf-ink/40 via-transparent to-transparent" aria-hidden="true" />
        </div>
      )}
      {data.intro && <p className="text-rf-ink text-lg leading-relaxed mb-6 max-w-3xl" data-testid="cuisine-intro">{data.intro}</p>}
      <p className="text-rf-ink-soft text-lg mb-12 max-w-2xl" data-testid="cuisine-count">
        {n === 0 ? `No public ${cuisine} recipes yet.` : `${n} ${n === 1 ? "recipe" : "recipes"} preserved by Recipe Filer cooks. Free to read, free to share.`}
      </p>
      {n > 0 ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="cuisine-recipe-grid">
          {recipes.map((r) => <RecipeCard key={r.recipe_id} recipe={r} />)}
        </div>
      ) : (
        <div className="card-recipe text-center py-12">
          <Link to="/directory" className="btn-amber">Browse all recipes</Link>
        </div>
      )}
      <FaqList name={cuisine} faqs={data.faqs || []} />
      {(data.others || []).length > 0 && (
        <section className="mt-16" data-testid="cuisine-others">
          <h2 className="font-display text-2xl font-black text-rf-ink mb-4">More cuisines</h2>
          <CuisineChips rows={data.others} />
        </section>
      )}
    </div>
  );
}
